import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../../context/AuthContext';
import { Button } from '../../components/ui/button';
import { Card, CardContent } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../../components/ui/dialog';
import { Separator } from '../../components/ui/separator';
import {
  ArrowLeft, Loader2, Repeat, Search, DollarSign, Plus, Pause, Play,
  Zap, Trash2, ExternalLink, Calendar as CalendarIcon, X, Check, AlertCircle
} from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { toast } from 'sonner';
import { AdminSubNav, SUBNAV_INVOICES } from '../../components/admin/AdminSubNav';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const STATUS_CONFIG = {
  active: { label: 'Active',  color: 'bg-emerald-50 text-emerald-700 border-emerald-200' },
  paused: { label: 'Paused',  color: 'bg-amber-50 text-amber-700 border-amber-200' },
  ended:  { label: 'Ended',   color: 'bg-zinc-100 text-zinc-500 border-zinc-200' },
};

const FREQUENCY_LABEL = {
  weekly: 'Weekly', biweekly: 'Bi-Weekly', monthly: 'Monthly', quarterly: 'Quarterly',
};

const STATUS_FILTERS = ['all', 'active', 'paused', 'ended'];

const formatDate = (iso) => iso ? new Date(iso).toLocaleDateString() : '—';
const fmtMoney = (n) => `$${Number(n || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

// ============ MAIN ============

const AdminRecurringInvoicesPage = () => {
  const { token } = useAuth();
  const navigate = useNavigate();
  const [schedules, setSchedules] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [showCreate, setShowCreate] = useState(false);
  const [selected, setSelected] = useState(null);
  const [detailOpen, setDetailOpen] = useState(false);
  const [actionLoading, setActionLoading] = useState('');

  const headers = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);

  const fetchAll = useCallback(async () => {
    try {
      const params = {};
      if (statusFilter !== 'all') params.status = statusFilter;
      if (search) params.search = search;
      const [list, st] = await Promise.all([
        axios.get(`${API_URL}/api/admin/recurring-invoices`, { headers, params }),
        axios.get(`${API_URL}/api/admin/recurring-invoices/stats`, { headers }),
      ]);
      setSchedules(list.data);
      setStats(st.data);
    } catch { toast.error('Failed to load recurring schedules'); }
    finally { setLoading(false); }
  }, [headers, statusFilter, search]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const openDetail = async (sch) => {
    try {
      const res = await axios.get(`${API_URL}/api/admin/recurring-invoices/${sch.id}`, { headers });
      setSelected(res.data);
      setDetailOpen(true);
    } catch { toast.error('Failed to load schedule'); }
  };

  const pauseResume = async (sch) => {
    setActionLoading(`toggle-${sch.id}`);
    try {
      const action = sch.status === 'active' ? 'pause' : 'resume';
      await axios.post(`${API_URL}/api/admin/recurring-invoices/${sch.id}/${action}`, {}, { headers });
      toast.success(action === 'pause' ? 'Schedule paused' : 'Schedule resumed');
      fetchAll();
      if (selected?.id === sch.id) openDetail(sch);
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
    finally { setActionLoading(''); }
  };

  const runNow = async (sch) => {
    setActionLoading(`run-${sch.id}`);
    try {
      const res = await axios.post(`${API_URL}/api/admin/recurring-invoices/${sch.id}/run-now`, {}, { headers });
      toast.success(`Invoice generated — next run ${formatDate(res.data.next_run_date)}`);
      fetchAll();
      openDetail(res.data);
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to generate invoice'); }
    finally { setActionLoading(''); }
  };

  const remove = async (sch) => {
    if (!window.confirm(`Delete recurring schedule "${sch.name}"? Past invoices stay intact.`)) return;
    setActionLoading(`del-${sch.id}`);
    try {
      await axios.delete(`${API_URL}/api/admin/recurring-invoices/${sch.id}`, { headers });
      toast.success('Schedule deleted');
      setDetailOpen(false);
      fetchAll();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
    finally { setActionLoading(''); }
  };

  if (loading) return (
    <div className="min-h-screen flex items-center justify-center bg-zinc-50">
      <Loader2 className="h-8 w-8 animate-spin text-[#F56F05]" />
    </div>
  );

  if (showCreate) return (
    <CreateScheduleForm headers={headers} onClose={() => setShowCreate(false)}
      onCreated={() => { setShowCreate(false); fetchAll(); }} />
  );

  const statCards = [
    { label: 'Active', value: stats?.active || 0, accent: 'text-emerald-600', bg: 'bg-emerald-50', icon: Repeat },
    { label: 'Paused', value: stats?.paused || 0, accent: 'text-amber-600', bg: 'bg-amber-50', icon: Pause },
    { label: 'Monthly Recurring', value: fmtMoney(stats?.monthly_recurring_revenue), accent: 'text-[#F56F05]', bg: 'bg-orange-50', icon: Zap },
    { label: 'Total Billed', value: fmtMoney(stats?.total_billed), accent: 'text-zinc-900', bg: 'bg-zinc-100', icon: DollarSign },
  ];

  return (
    <div className="min-h-screen bg-zinc-50" data-testid="admin-recurring-invoices-page">
      {/* Header */}
      <header className="bg-white border-b border-zinc-100 sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="icon" onClick={() => navigate('/admin')} data-testid="back-btn">
              <ArrowLeft className="h-5 w-5" />
            </Button>
            <div>
              <h1 className="text-xl font-bold text-zinc-900">Recurring Invoices</h1>
              <p className="text-xs text-zinc-500">{schedules.length} schedules · auto-bills via Stripe daily at 6:30am PT</p>
            </div>
          </div>
          <Button onClick={() => setShowCreate(true)} className="bg-[#F56F05] hover:bg-[#e06505] text-white rounded-xl" data-testid="create-schedule-btn">
            <Plus className="h-4 w-4 mr-2" /> New Contract
          </Button>
        </div>
      </header>
      <AdminSubNav section="Billing" items={SUBNAV_INVOICES} />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-6 space-y-6">
        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {statCards.map(s => (
            <Card key={s.label} className="border-zinc-100" data-testid={`stat-${s.label.toLowerCase().replace(/ /g,'-')}`}>
              <CardContent className="p-4 flex items-center gap-3">
                <div className={`w-10 h-10 rounded-xl ${s.bg} flex items-center justify-center`}>
                  <s.icon className={`h-5 w-5 ${s.accent}`} />
                </div>
                <div>
                  <p className="text-xs text-zinc-500">{s.label}</p>
                  <p className={`text-lg font-bold ${s.accent}`}>{s.value}</p>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Filters */}
        <div className="flex flex-col sm:flex-row gap-3">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-zinc-400" />
            <Input placeholder="Search by name, client, email..." value={search}
              onChange={e => setSearch(e.target.value)}
              className="pl-9 rounded-xl border-zinc-200" data-testid="search-input" />
          </div>
          <div className="flex gap-2 flex-wrap">
            {STATUS_FILTERS.map(s => (
              <Button key={s} variant={statusFilter === s ? 'default' : 'outline'} size="sm"
                className={`rounded-full text-xs capitalize ${statusFilter === s ? 'bg-zinc-900 text-white' : 'border-zinc-200'}`}
                onClick={() => setStatusFilter(s)} data-testid={`filter-${s}`}>
                {s === 'all' ? 'All' : STATUS_CONFIG[s]?.label || s}
              </Button>
            ))}
          </div>
        </div>

        {/* Table */}
        {schedules.length === 0 ? (
          <Card className="border-zinc-100 border-dashed">
            <CardContent className="py-16 text-center">
              <Repeat className="h-10 w-10 text-zinc-300 mx-auto mb-3" />
              <h3 className="font-bold text-zinc-900 mb-1">No recurring contracts yet</h3>
              <p className="text-sm text-zinc-500 mb-4">Set up auto-billing for commercial accounts and never chase an invoice again.</p>
              <Button onClick={() => setShowCreate(true)} className="bg-[#F56F05] hover:bg-[#e06505] text-white rounded-xl">
                <Plus className="h-4 w-4 mr-2" /> New Contract
              </Button>
            </CardContent>
          </Card>
        ) : (
          <Card className="border-zinc-100 overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full" data-testid="schedules-table">
                <thead>
                  <tr className="border-b border-zinc-100 bg-zinc-50/50">
                    <th className="text-left text-xs font-semibold text-zinc-500 px-4 py-3">Contract</th>
                    <th className="text-left text-xs font-semibold text-zinc-500 px-4 py-3">Client</th>
                    <th className="text-left text-xs font-semibold text-zinc-500 px-4 py-3 hidden md:table-cell">Cadence</th>
                    <th className="text-left text-xs font-semibold text-zinc-500 px-4 py-3 hidden lg:table-cell">Next Run</th>
                    <th className="text-right text-xs font-semibold text-zinc-500 px-4 py-3">Per-Invoice</th>
                    <th className="text-center text-xs font-semibold text-zinc-500 px-4 py-3">Status</th>
                    <th className="text-right text-xs font-semibold text-zinc-500 px-4 py-3">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {schedules.map(sch => {
                    const cfg = STATUS_CONFIG[sch.status] || STATUS_CONFIG.active;
                    const perAmt = (sch.line_items || []).reduce((s, i) => s + (i.unit_price * i.quantity), 0) + (sch.tax_amount || 0);
                    return (
                      <tr key={sch.id} className="border-b border-zinc-50 hover:bg-zinc-50/50 cursor-pointer transition-colors"
                        onClick={() => openDetail(sch)} data-testid={`schedule-row-${sch.id}`}>
                        <td className="px-4 py-3">
                          <p className="font-semibold text-sm text-zinc-900">{sch.name}</p>
                          <p className="text-[11px] text-zinc-400">{sch.service_type || '—'}</p>
                        </td>
                        <td className="px-4 py-3">
                          <p className="text-sm font-medium text-zinc-800">{sch.client_name}</p>
                          <p className="text-[11px] text-zinc-400">{sch.client_email}</p>
                        </td>
                        <td className="px-4 py-3 hidden md:table-cell">
                          <Badge variant="outline" className="text-[10px] border-zinc-200">{FREQUENCY_LABEL[sch.frequency] || sch.frequency}</Badge>
                        </td>
                        <td className="px-4 py-3 hidden lg:table-cell">
                          <p className="text-xs text-zinc-600">{formatDate(sch.next_run_date)}</p>
                          {sch.invoices_generated > 0 && <p className="text-[10px] text-zinc-400">{sch.invoices_generated} sent</p>}
                        </td>
                        <td className="px-4 py-3 text-right">
                          <p className="font-bold text-sm text-zinc-900">{fmtMoney(perAmt)}</p>
                        </td>
                        <td className="px-4 py-3 text-center">
                          <Badge className={`${cfg.color} border text-[10px] font-semibold`}>{cfg.label}</Badge>
                        </td>
                        <td className="px-4 py-3 text-right" onClick={e => e.stopPropagation()}>
                          <div className="flex items-center justify-end gap-1">
                            {sch.status !== 'ended' && (
                              <Button variant="ghost" size="icon" className="h-8 w-8"
                                onClick={() => pauseResume(sch)} disabled={actionLoading === `toggle-${sch.id}`}
                                title={sch.status === 'active' ? 'Pause' : 'Resume'}
                                data-testid={`toggle-${sch.id}`}>
                                {actionLoading === `toggle-${sch.id}` ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> :
                                  (sch.status === 'active' ? <Pause className="h-3.5 w-3.5 text-zinc-400" /> : <Play className="h-3.5 w-3.5 text-emerald-500" />)}
                              </Button>
                            )}
                            {sch.status === 'active' && (
                              <Button variant="ghost" size="icon" className="h-8 w-8"
                                onClick={() => runNow(sch)} disabled={actionLoading === `run-${sch.id}`}
                                title="Generate invoice now" data-testid={`run-${sch.id}`}>
                                {actionLoading === `run-${sch.id}` ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Zap className="h-3.5 w-3.5 text-[#F56F05]" />}
                              </Button>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </Card>
        )}
      </div>

      <ScheduleDetailModal sch={selected} open={detailOpen} onClose={() => setDetailOpen(false)}
        onPauseResume={pauseResume} onRunNow={runNow} onDelete={remove} actionLoading={actionLoading} />
    </div>
  );
};

// ============ DETAIL ============

const ScheduleDetailModal = ({ sch, open, onClose, onPauseResume, onRunNow, onDelete, actionLoading }) => {
  if (!sch) return null;
  const cfg = STATUS_CONFIG[sch.status] || STATUS_CONFIG.active;
  const perAmt = (sch.line_items || []).reduce((s, i) => s + (i.unit_price * i.quantity), 0) + (sch.tax_amount || 0);

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="schedule-detail-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center justify-between gap-3">
            <span className="truncate">{sch.name}</span>
            <Badge className={`${cfg.color} border text-xs shrink-0`}>{cfg.label}</Badge>
          </DialogTitle>
          <DialogDescription>{FREQUENCY_LABEL[sch.frequency]} · {sch.service_type || 'Commercial'}</DialogDescription>
        </DialogHeader>

        <div className="space-y-4 mt-2">
          {/* Last run error */}
          {sch.last_run_status === 'failed' && sch.last_run_error && (
            <div className="bg-red-50 border border-red-200 rounded-xl p-3 flex gap-2 text-xs text-red-700" data-testid="run-error">
              <AlertCircle className="h-4 w-4 shrink-0 mt-0.5" />
              <div><p className="font-semibold mb-0.5">Last run failed</p><p>{sch.last_run_error}</p></div>
            </div>
          )}

          {/* Client */}
          <div className="bg-zinc-50 rounded-xl p-4 space-y-1">
            <p className="font-bold text-sm text-zinc-900">{sch.client_name}</p>
            <p className="text-xs text-zinc-500">{sch.client_email}</p>
            {sch.client_phone && <p className="text-xs text-zinc-500">{sch.client_phone}</p>}
            {sch.property_address && <p className="text-xs text-zinc-400 mt-1">{sch.property_address}</p>}
          </div>

          {/* KPIs */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            <KPI label="Next Run" value={formatDate(sch.next_run_date)} />
            <KPI label="Last Run" value={sch.last_run_at ? new Date(sch.last_run_at).toLocaleDateString() : '—'} />
            <KPI label="Invoices Sent" value={sch.invoices_generated || 0} />
            <KPI label="Total Billed" value={fmtMoney(sch.total_billed)} accent />
          </div>

          {/* Schedule meta */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-xs">
            <Meta label="Start" value={formatDate(sch.start_date)} />
            <Meta label="End" value={sch.end_date ? formatDate(sch.end_date) : 'Open-ended'} />
            <Meta label="Terms" value={`Net ${sch.payment_terms_days || 7}`} />
            <Meta label="Auto-send" value={sch.auto_send ? 'Yes' : 'Draft only'} />
          </div>

          <Separator />

          {/* Line items */}
          <div>
            <p className="text-xs font-semibold text-zinc-500 mb-2 uppercase tracking-wider">Line Items (per invoice)</p>
            <div className="space-y-2">
              {sch.line_items?.map((it, i) => (
                // eslint-disable-next-line react/no-array-index-key
                <div key={i} className="flex justify-between text-sm">
                  <span className="text-zinc-700">{it.description} <span className="text-zinc-400">x{it.quantity}</span></span>
                  <span className="font-medium">{fmtMoney(it.unit_price * it.quantity)}</span>
                </div>
              ))}
              {sch.tax_amount > 0 && (
                <div className="flex justify-between text-sm">
                  <span className="text-zinc-500">Tax / Fee</span><span>{fmtMoney(sch.tax_amount)}</span>
                </div>
              )}
            </div>
            <Separator className="my-2" />
            <div className="flex justify-between font-bold text-base">
              <span>Per Invoice</span><span className="text-[#F56F05]">{fmtMoney(perAmt)}</span>
            </div>
          </div>

          {/* Past invoices */}
          {sch.invoices?.length > 0 && (
            <div>
              <p className="text-xs font-semibold text-zinc-500 mb-2 uppercase tracking-wider">Generated Invoices</p>
              <div className="space-y-1.5 max-h-48 overflow-y-auto">
                {sch.invoices.map(inv => (
                  <div key={inv.id} className="flex items-center justify-between bg-zinc-50 rounded-lg px-3 py-2 text-xs" data-testid={`past-inv-${inv.id}`}>
                    <div>
                      <p className="font-medium text-zinc-800">{inv.invoice_number}</p>
                      <p className="text-zinc-400">{new Date(inv.created_at).toLocaleDateString()} · {inv.status}</p>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="font-bold text-zinc-900">{fmtMoney(inv.total)}</span>
                      {inv.hosted_invoice_url && (
                        <a href={inv.hosted_invoice_url} target="_blank" rel="noopener noreferrer">
                          <ExternalLink className="h-3.5 w-3.5 text-[#F56F05]" />
                        </a>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {sch.notes && <div><p className="text-xs text-zinc-400 mb-1">Notes (visible to client)</p><p className="text-sm bg-zinc-50 rounded-lg p-3">{sch.notes}</p></div>}
          {sch.internal_notes && <div><p className="text-xs text-zinc-400 mb-1">Internal Notes</p><p className="text-sm bg-amber-50 rounded-lg p-3 text-amber-800">{sch.internal_notes}</p></div>}

          <Separator />

          {/* Actions */}
          <div className="grid grid-cols-2 gap-2">
            {sch.status !== 'ended' && (
              <Button variant="outline" className="rounded-xl text-sm h-10"
                onClick={() => onPauseResume(sch)} disabled={actionLoading === `toggle-${sch.id}`} data-testid="detail-toggle">
                {actionLoading === `toggle-${sch.id}` ? <Loader2 className="h-3.5 w-3.5 mr-2 animate-spin" /> :
                  (sch.status === 'active' ? <Pause className="h-3.5 w-3.5 mr-2" /> : <Play className="h-3.5 w-3.5 mr-2" />)}
                {sch.status === 'active' ? 'Pause Schedule' : 'Resume Schedule'}
              </Button>
            )}
            {sch.status === 'active' && (
              <Button className="bg-[#F56F05] hover:bg-[#e06505] text-white rounded-xl text-sm h-10"
                onClick={() => onRunNow(sch)} disabled={actionLoading === `run-${sch.id}`} data-testid="detail-run-now">
                {actionLoading === `run-${sch.id}` ? <Loader2 className="h-3.5 w-3.5 mr-2 animate-spin" /> : <Zap className="h-3.5 w-3.5 mr-2" />}
                Bill Now
              </Button>
            )}
            <Button variant="outline" className="rounded-xl text-sm h-10 text-red-600 border-red-200 hover:bg-red-50 col-span-2 sm:col-span-1"
              onClick={() => onDelete(sch)} disabled={actionLoading === `del-${sch.id}`} data-testid="detail-delete">
              {actionLoading === `del-${sch.id}` ? <Loader2 className="h-3.5 w-3.5 mr-2 animate-spin" /> : <Trash2 className="h-3.5 w-3.5 mr-2" />}
              Delete Schedule
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

const KPI = ({ label, value, accent }) => (
  <div className="bg-white border border-zinc-100 rounded-xl p-3">
    <p className="text-[10px] uppercase tracking-wider text-zinc-400 font-semibold">{label}</p>
    <p className={`text-sm font-bold mt-1 ${accent ? 'text-[#F56F05]' : 'text-zinc-900'}`}>{value}</p>
  </div>
);

const Meta = ({ label, value }) => (
  <div><p className="text-zinc-400">{label}</p><p className="font-medium text-zinc-700">{value}</p></div>
);

// ============ CREATE FORM ============

const CreateScheduleForm = ({ headers, onClose, onCreated }) => {
  const [submitting, setSubmitting] = useState(false);
  const todayStr = useMemo(() => new Date().toISOString().slice(0, 10), []);
  const [form, setForm] = useState({
    name: '', client_name: '', client_email: '', client_phone: '',
    property_address: '', service_type: 'Commercial Cleaning',
    frequency: 'monthly', start_date: todayStr, end_date: '',
    line_items: [{ _key: Date.now(), description: '', quantity: 1, unit_price: 0 }],
    tax_amount: 0, payment_terms_days: 14, auto_send: true,
    notes: '', internal_notes: '',
  });

  const set = (f, v) => setForm(p => ({ ...p, [f]: v }));
  const updItem = (i, k, v) => setForm(p => {
    const items = [...p.line_items]; items[i] = { ...items[i], [k]: v }; return { ...p, line_items: items };
  });
  const addItem = () => setForm(p => ({ ...p, line_items: [...p.line_items, { _key: Date.now() + Math.random(), description: '', quantity: 1, unit_price: 0 }] }));
  const rmItem = (i) => {
    if (form.line_items.length <= 1) return;
    setForm(p => ({ ...p, line_items: p.line_items.filter((_, idx) => idx !== i) }));
  };

  const subtotal = form.line_items.reduce((s, i) => s + (parseFloat(i.unit_price) || 0) * (parseInt(i.quantity) || 1), 0);
  const total = subtotal + (parseFloat(form.tax_amount) || 0);

  const canSubmit = form.name && form.client_name && form.client_email && form.start_date
    && form.line_items.some(i => i.description && parseFloat(i.unit_price) > 0);

  const submit = async () => {
    if (!canSubmit) return;
    setSubmitting(true);
    try {
      const payload = {
        ...form,
        tax_amount: parseFloat(form.tax_amount) || 0,
        payment_terms_days: parseInt(form.payment_terms_days) || 7,
        end_date: form.end_date || null,
        line_items: form.line_items.filter(i => i.description).map(i => ({
          description: i.description, quantity: parseInt(i.quantity) || 1, unit_price: parseFloat(i.unit_price) || 0,
        })),
      };
      await axios.post(`${API_URL}/api/admin/recurring-invoices`, payload, { headers });
      toast.success('Recurring contract created');
      onCreated();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to create schedule'); }
    finally { setSubmitting(false); }
  };

  const FREQ_OPTIONS = [
    { id: 'weekly', label: 'Weekly', sub: 'Every 7 days' },
    { id: 'biweekly', label: 'Bi-Weekly', sub: 'Every 14 days' },
    { id: 'monthly', label: 'Monthly', sub: 'Same day each month' },
    { id: 'quarterly', label: 'Quarterly', sub: 'Every 3 months' },
  ];

  return (
    <div className="min-h-screen bg-zinc-50" data-testid="create-schedule-form">
      <div className="bg-white border-b border-zinc-100 sticky top-0 z-10">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="icon" onClick={onClose} data-testid="close-create-form"><ArrowLeft className="h-5 w-5" /></Button>
            <h1 className="text-xl font-bold text-zinc-900">New Recurring Contract</h1>
          </div>
          <Button onClick={submit} disabled={!canSubmit || submitting}
            className="bg-[#F56F05] hover:bg-[#e06505] text-white rounded-xl" data-testid="submit-schedule-btn">
            {submitting ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Check className="h-4 w-4 mr-2" />}
            Create Contract
          </Button>
        </div>
      </div>

      <div className="max-w-3xl mx-auto px-4 sm:px-6 py-6 space-y-6">
        {/* Contract */}
        <Card className="border-zinc-100"><CardContent className="p-4 space-y-4">
          <p className="text-sm font-semibold text-zinc-700">Contract Details</p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <Label className="text-xs text-zinc-500">Contract Name *</Label>
              <Input value={form.name} onChange={e => set('name', e.target.value)}
                className="mt-1 rounded-xl border-zinc-200" placeholder="ACME Corp - HQ Cleaning" data-testid="contract-name" />
            </div>
            <div>
              <Label className="text-xs text-zinc-500">Service Type</Label>
              <Input value={form.service_type} onChange={e => set('service_type', e.target.value)}
                className="mt-1 rounded-xl border-zinc-200" placeholder="Commercial Cleaning" data-testid="service-type" />
            </div>
          </div>
        </CardContent></Card>

        {/* Client */}
        <Card className="border-zinc-100"><CardContent className="p-4 space-y-4">
          <p className="text-sm font-semibold text-zinc-700">Client</p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <Label className="text-xs text-zinc-500">Client Name *</Label>
              <Input value={form.client_name} onChange={e => set('client_name', e.target.value)}
                className="mt-1 rounded-xl border-zinc-200" placeholder="ACME Corporation" data-testid="client-name" />
            </div>
            <div>
              <Label className="text-xs text-zinc-500">Billing Email *</Label>
              <Input type="email" value={form.client_email} onChange={e => set('client_email', e.target.value)}
                className="mt-1 rounded-xl border-zinc-200" placeholder="billing@acme.com" data-testid="client-email" />
            </div>
            <div>
              <Label className="text-xs text-zinc-500">Phone</Label>
              <Input value={form.client_phone} onChange={e => set('client_phone', e.target.value)}
                className="mt-1 rounded-xl border-zinc-200" placeholder="(503) 555-1234" data-testid="client-phone" />
            </div>
            <div>
              <Label className="text-xs text-zinc-500">Property Address</Label>
              <Input value={form.property_address} onChange={e => set('property_address', e.target.value)}
                className="mt-1 rounded-xl border-zinc-200" placeholder="Pearl District, Portland OR" data-testid="property-address" />
            </div>
          </div>
        </CardContent></Card>

        {/* Cadence */}
        <Card className="border-zinc-100"><CardContent className="p-4 space-y-4">
          <p className="text-sm font-semibold text-zinc-700">Billing Cadence</p>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
            {FREQ_OPTIONS.map(f => (
              <button key={f.id} onClick={() => set('frequency', f.id)} type="button"
                className={`p-3 rounded-xl border-2 text-left transition-all ${form.frequency === f.id ? 'border-[#F56F05] bg-orange-50' : 'border-zinc-100 hover:border-zinc-200'}`}
                data-testid={`frequency-${f.id}`}>
                <p className={`text-sm font-bold ${form.frequency === f.id ? 'text-[#F56F05]' : 'text-zinc-800'}`}>{f.label}</p>
                <p className="text-[11px] text-zinc-400 mt-0.5">{f.sub}</p>
              </button>
            ))}
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <div>
              <Label className="text-xs text-zinc-500">First Invoice Date *</Label>
              <Input type="date" value={form.start_date} onChange={e => set('start_date', e.target.value)}
                className="mt-1 rounded-xl border-zinc-200" data-testid="start-date" />
            </div>
            <div>
              <Label className="text-xs text-zinc-500">End Date (optional)</Label>
              <Input type="date" value={form.end_date} onChange={e => set('end_date', e.target.value)}
                className="mt-1 rounded-xl border-zinc-200" data-testid="end-date" />
            </div>
            <div>
              <Label className="text-xs text-zinc-500">Payment Terms (days)</Label>
              <Input type="number" min="1" value={form.payment_terms_days} onChange={e => set('payment_terms_days', e.target.value)}
                className="mt-1 rounded-xl border-zinc-200" data-testid="payment-terms" />
            </div>
          </div>
          <label className="flex items-center gap-2 text-sm text-zinc-700 cursor-pointer">
            <input type="checkbox" checked={form.auto_send} onChange={e => set('auto_send', e.target.checked)} data-testid="auto-send" />
            Auto-email invoice to client via Stripe
          </label>
        </CardContent></Card>

        {/* Line items */}
        <Card className="border-zinc-100"><CardContent className="p-4 space-y-3">
          <div className="flex items-center justify-between">
            <p className="text-sm font-semibold text-zinc-700">Line Items (charged per invoice)</p>
            <Button variant="outline" size="sm" onClick={addItem} className="rounded-lg text-xs h-8" data-testid="add-line-item">
              <Plus className="h-3 w-3 mr-1" /> Add Item
            </Button>
          </div>
          {form.line_items.map((it, i) => (
            <div key={it._key} className="flex items-start gap-2" data-testid={`line-item-${i}`}>
              <Input value={it.description} onChange={e => updItem(i, 'description', e.target.value)}
                className="flex-1 rounded-xl border-zinc-200 text-sm" placeholder="Monthly Office Cleaning" data-testid={`line-desc-${i}`} />
              <Input type="number" min="1" value={it.quantity} onChange={e => updItem(i, 'quantity', e.target.value)}
                className="w-16 rounded-xl border-zinc-200 text-sm text-center" data-testid={`line-qty-${i}`} />
              <Input type="number" min="0" step="0.01" value={it.unit_price} onChange={e => updItem(i, 'unit_price', e.target.value)}
                className="w-24 rounded-xl border-zinc-200 text-sm text-right" placeholder="0.00" data-testid={`line-price-${i}`} />
              {form.line_items.length > 1 && (
                <Button variant="ghost" size="icon" className="h-9 w-9 text-zinc-400 hover:text-red-500" onClick={() => rmItem(i)}>
                  <X className="h-4 w-4" />
                </Button>
              )}
            </div>
          ))}
          <Separator />
          <div className="space-y-2 text-sm">
            <div className="flex justify-between"><span className="text-zinc-500">Subtotal</span><span className="font-medium">{fmtMoney(subtotal)}</span></div>
            <div className="flex items-center justify-between">
              <Label className="text-zinc-500 text-sm">Tax / Fee</Label>
              <Input type="number" min="0" step="0.01" value={form.tax_amount}
                onChange={e => set('tax_amount', e.target.value)}
                className="w-24 rounded-xl border-zinc-200 text-sm text-right h-8" data-testid="tax-amount" />
            </div>
            <Separator />
            <div className="flex justify-between font-bold text-base">
              <span>Per Invoice</span><span className="text-[#F56F05]">{fmtMoney(total)}</span>
            </div>
          </div>
        </CardContent></Card>

        {/* Notes */}
        <Card className="border-zinc-100"><CardContent className="p-4 space-y-3">
          <div>
            <Label className="text-xs text-zinc-500">Client-Facing Notes</Label>
            <Textarea value={form.notes} onChange={e => set('notes', e.target.value)}
              className="mt-1 rounded-xl border-zinc-200" rows={2} placeholder="Note that appears on each invoice..." data-testid="notes" />
          </div>
          <div>
            <Label className="text-xs text-zinc-500">Internal Notes</Label>
            <Textarea value={form.internal_notes} onChange={e => set('internal_notes', e.target.value)}
              className="mt-1 rounded-xl border-zinc-200" rows={2} placeholder="Admin-only notes..." data-testid="internal-notes" />
          </div>
        </CardContent></Card>

        <div className="flex justify-end gap-3 pb-8">
          <Button variant="outline" onClick={onClose} className="rounded-xl" data-testid="cancel-create">Cancel</Button>
          <Button onClick={submit} disabled={!canSubmit || submitting}
            className="bg-[#F56F05] hover:bg-[#e06505] text-white rounded-xl px-8" data-testid="submit-schedule-bottom">
            {submitting ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Check className="h-4 w-4 mr-2" />}
            Create Contract
          </Button>
        </div>
      </div>
    </div>
  );
};

export default AdminRecurringInvoicesPage;
